import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { isObject, invert } from 'underscore';
import I18n from 'I18n';
import { clean, stripHtmlFromString } from 'ContentComponents/components/cmv2/sanitize/sanitize';
import { extractFileName } from 'ContentComponents/utils/fileUtils';
import { isNullOrUndefined } from 'ContentUtils/helpers/ObjectHelpers';
import { valueIsArray } from 'ContentUtils/helpers/FieldHelpers';
import { RESOURCES_TO_FETCH, RESOURCE_GETTERS, RESOURCE_FETCHERS, FETCH_STATUS_GETTERS } from 'ContentComponents/components/cmv2/occurrences/constants';
import { getFieldDataForLabel, shouldFetchResource } from './utils';
import * as FieldTypes from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { EmbedTypes } from 'ContentUtils/constants/MediaTypes';
import { FONT_FORMAT_LIST } from 'ContentUtils/constants/Fonts';
import { Image } from 'foundations-components/transitional/components/Image';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { ColorSwatch } from 'foundations-components/transitional/components/ColorSwatch';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import Icon from 'ContentComponents/components/cmv2/iconPicker/Icon';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SYSTEM_FONTS_BY_VALUE = invert(FONT_FORMAT_LIST);
const ICON_SIZE = 20;
function mapStateToProps(state, props) {
  const fieldData = getFieldDataForLabel(props.value, props.field);
  const type = props.field.type;
  if (!(type && fieldData) || !RESOURCES_TO_FETCH[type]) return {};
  const getResource = RESOURCE_GETTERS[type];
  const getResourceStatus = FETCH_STATUS_GETTERS[type];
  return {
    resource: getResource(state, fieldData),
    status: getResourceStatus(state, fieldData)
  };
}
const mapDispatchToProps = (dispatch, props) => {
  const type = props.field.type;
  if (!type || !RESOURCES_TO_FETCH[type]) return {};
  const fetchResource = RESOURCE_FETCHERS[type];
  return {
    fetchResource: fieldData => dispatch(fetchResource(fieldData))
  };
};
class OccurrenceLabel extends Component {
  constructor(...args) {
    super(...args);
    this.ConnectedOccurrenceLabel = connect(mapStateToProps, mapDispatchToProps)(OccurrenceLabel);
  }
  componentDidMount() {
    const {
      fetchResource,
      field,
      resource,
      status,
      value
    } = this.props;
    if (!resource && fetchResource && shouldFetchResource(status)) {
      const fieldData = getFieldDataForLabel(value, field);
      if (fieldData) fetchResource(fieldData);
    }
  }
  renderConnectedResource(resourceObject) {
    let label = resourceObject.label || resourceObject.name;
    if (this.props.field.type === FieldTypes.CTA) {
      label = resourceObject.name;
    }
    if (this.props.field.type === FieldTypes.ICON) {
      return this.renderIcon();
    }
    return /*#__PURE__*/_jsx("span", {
      children: label
    });
  }
  renderGroup(value) {
    const {
      field,
      asString,
      resources,
      defaultLabel
    } = this.props;
    if (!isObject(value) || !Object.keys(value).length || !field.children) {
      return /*#__PURE__*/_jsx("span", {
        children: defaultLabel
      });
    }
    let childField = field.occurrence && field.occurrence.sorting_label_field && field.children.find(_childField => _childField.id === field.occurrence.sorting_label_field);
    childField = childField || field.children[0];
    if (!childField) {
      return /*#__PURE__*/_jsx("span", {
        children: defaultLabel
      });
    }
    return /*#__PURE__*/_jsx(this.ConnectedOccurrenceLabel, {
      defaultLabel: defaultLabel,
      value: value[childField.name],
      field: childField,
      resources: resources,
      asString: asString
    });
  }
  renderImage(value) {
    const {
      asString,
      defaultLabel
    } = this.props;
    let imageLabel = value.alt;
    if (!imageLabel && value.src) {
      const srcFilePath = value.src.split('/');
      imageLabel = srcFilePath[srcFilePath.length - 1];
    } else if (!imageLabel) {
      imageLabel = defaultLabel;
    }
    if (asString || !value.src) {
      return /*#__PURE__*/_jsx("span", {
        children: imageLabel
      });
    }
    return /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(Box, {
        shrink: 0,
        basis: ICON_SIZE,
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(Image, {
          src: value.src
        })
      }), imageLabel]
    });
  }
  renderIcon() {
    const {
      asString,
      resource,
      defaultLabel,
      value
    } = this.props;
    if (asString || !value || !resource) {
      return /*#__PURE__*/_jsx("span", {
        children: defaultLabel
      });
    }
    const {
      name,
      type
    } = value;
    const {
      unicode,
      viewPort,
      categories,
      svg
    } = resource;
    const iconData = {
      name,
      unicode,
      viewPort,
      svg,
      type,
      categories
    };
    return /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(Box, {
        shrink: 0,
        basis: ICON_SIZE,
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(Icon, {
          height: ICON_SIZE,
          iconData: iconData,
          width: ICON_SIZE
        })
      }), name]
    });
  }
  renderColor(value) {
    const {
      asString
    } = this.props;
    value = typeof value === 'string' ? value : value.color;
    if (asString) {
      return /*#__PURE__*/_jsx("span", {
        children: value
      });
    }
    return /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      children: [/*#__PURE__*/_jsx(ColorSwatch, {
        color: value,
        borderColor: "border-core-default",
        borderWidth: "100",
        marginInlineEnd: "100",
        size: 10
      }), value]
    });
  }
  renderRichText(value) {
    var _DOMParser$parseFromS;
    const {
      asString,
      defaultLabel
    } = this.props;
    const stripped = stripHtmlFromString(clean(value)).trim();
    const decoded = stripped ? (_DOMParser$parseFromS = new DOMParser().parseFromString(stripped, 'text/html').body.textContent) !== null && _DOMParser$parseFromS !== void 0 ? _DOMParser$parseFromS : stripped : '';
    const plainText = decoded || defaultLabel;
    if (asString) {
      return /*#__PURE__*/_jsx("span", {
        children: plainText
      });
    }
    return /*#__PURE__*/_jsx(TruncateString, {
      children: /*#__PURE__*/_jsx("span", {
        children: plainText
      })
    });
  }
  renderChoice(field, value) {
    let choiceLabel = value;
    if (!this.choicesCache) {
      // should be kept consistent with the Choice field's getOptionsData function
      const _choices = value.choices ? value.choices : field.choices;
      this.choicesCache = _choices && _choices.reduce((acc, _choice) => {
        if (_choice[1]) {
          if (Array.isArray(_choice[1])) {
            // if there are categories _choice[1] will be the array of options
            _choice[1].forEach(opt => {
              acc[opt[0]] = {
                text: opt[1],
                value: opt[0]
              };
            });
          } else {
            acc[_choice[0]] = {
              text: _choice[1],
              value: _choice[0]
            };
          }
        }
        return acc;
      }, {});
    }
    if (field.multiple && Array.isArray(value) && value.length > 1) {
      choiceLabel = value.map(val => {
        const opt = this.choicesCache[val] || {
          text: val,
          value: val
        };
        const text = opt.text || opt.value;
        const tagUse = opt.tagUse || 'default';
        return /*#__PURE__*/_jsx(Tag, {
          closeable: false,
          use: tagUse,
          children: /*#__PURE__*/_jsx("div", {
            dangerouslySetInnerHTML: {
              __html: clean(`${text}`)
            },
            title: `${text}`
          })
        }, val);
      });
    } else {
      const choice = this.choicesCache[value];
      const text = choice ? choice.text || choice.value : choiceLabel;
      choiceLabel = /*#__PURE__*/_jsx("span", {
        dangerouslySetInnerHTML: {
          __html: clean(text)
        },
        title: text
      });
    }
    return /*#__PURE__*/_jsx(Fragment, {
      children: choiceLabel
    });
  }
  render() {
    const {
      field,
      resources,
      asString,
      defaultLabel,
      renderLabel,
      resource: resourceObject
    } = this.props;
    let {
      value
    } = this.props;
    if (value === undefined && field.default !== undefined) {
      value = field.default;
    }
    if (renderLabel) {
      return renderLabel(value, field, defaultLabel);
    }
    if (isNullOrUndefined(value) || value === '' || field.type === FieldTypes.SIMPLE_MENU) {
      return /*#__PURE__*/_jsx("span", {
        children: defaultLabel
      });
    }
    if (Array.isArray(value) && field.type !== FieldTypes.CHOICE) {
      if (!value.length) {
        return /*#__PURE__*/_jsx("span", {
          children: defaultLabel
        });
      }
      if (valueIsArray(field) && !Array.isArray(value[0])) {
        return /*#__PURE__*/_jsx("span", {
          children: value.join(', ')
        });
      }
      return /*#__PURE__*/_jsx(this.ConnectedOccurrenceLabel, {
        defaultLabel: defaultLabel,
        value: value[0],
        field: field,
        resources: resources,
        asString: asString
      });
    }
    if (resourceObject) return this.renderConnectedResource(resourceObject);
    switch (field.type) {
      case FieldTypes.GROUP:
        {
          return this.renderGroup(value);
        }
      case FieldTypes.LOGO:
      case FieldTypes.IMAGE:
        {
          return this.renderImage(value);
        }
      case FieldTypes.COLOR:
        {
          return this.renderColor(value);
        }
      case FieldTypes.ICON:
        {
          return this.renderIcon();
        }
      case FieldTypes.CHOICE:
        {
          return this.renderChoice(field, value);
        }
      case FieldTypes.DATE:
        {
          return value ? /*#__PURE__*/_jsx("span", {
            children: I18n.moment.portalTz(value).format('L')
          }) : /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.DATE_TIME:
        {
          return value ? /*#__PURE__*/_jsx("span", {
            children: I18n.moment.portalTz(value).format('lll')
          }) : /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.FILE:
        {
          return /*#__PURE__*/_jsx("span", {
            children: extractFileName(value)
          });
        }
      case FieldTypes.RICH_TEXT:
        {
          return this.renderRichText(value);
        }
      case FieldTypes.FORM:
      case FieldTypes.SURVEY:
      case FieldTypes.BOOLEAN:
      case FieldTypes.VIDEO:
      case FieldTypes.CRM_OBJECT:
      case FieldTypes.CRM_OBJECT_PROPERTY:
      case FieldTypes.PAYMENT:
      case FieldTypes.HUBDB_ROW:
        {
          return /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.TAG:
        {
          return isObject(value) ? /*#__PURE__*/_jsx("span", {
            children: value.name
          }) : /*#__PURE__*/_jsx("span", {
            children: value
          });
        }
      case FieldTypes.URL:
        {
          const href = isObject(value) && value.href;
          return href ? /*#__PURE__*/_jsx("span", {
            children: href
          }) : /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.LINK:
        {
          const href = isObject(value) && value.url && value.url.href;
          return href ? /*#__PURE__*/_jsx("span", {
            children: href
          }) : /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.LOCATION:
        {
          const location = isObject(value) && value.lat && value.long;
          return location ? /*#__PURE__*/_jsx("span", {
            children: location
          }) : /*#__PURE__*/_jsx("span", {
            children: defaultLabel
          });
        }
      case FieldTypes.FONT:
        {
          const font = isObject(value) && value.font;
          if (!font) {
            return /*#__PURE__*/_jsx("span", {
              children: defaultLabel
            });
          }
          const systemFont = SYSTEM_FONTS_BY_VALUE[font];
          return /*#__PURE__*/_jsx("span", {
            children: systemFont || font
          });
        }
      case FieldTypes.EMBED:
        {
          const embedType = isObject(value) && value.source_type;
          let embedLabel = defaultLabel;
          if (embedType === EmbedTypes.OEMBED && value.oembed_url) {
            embedLabel = value.oembed_url;
          } else if (embedType === EmbedTypes.HTML && value.embed_html) {
            embedLabel = value.embed_html;
          }
          return /*#__PURE__*/_jsx("span", {
            children: embedLabel
          });
        }
      default:
        {
          if (typeof value === 'object') {
            return /*#__PURE__*/_jsx("span", {
              children: JSON.stringify(value)
            });
          }
          return /*#__PURE__*/_jsx("span", {
            children: value
          });
        }
    }
  }
}
OccurrenceLabel.defaultProps = {
  asString: false,
  resources: {}
};
export default connect(mapStateToProps, mapDispatchToProps)(OccurrenceLabel);